import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import Sidebar from '../components/Sidebar';
import useNotifications from '../hooks/useNotifications';
import { Package, Menu } from 'lucide-react';

// Extracted section components
import ClientOverview from './client/ClientOverview';
import ClientPackages, { ClientWarehouseReceipts, ClientTrackPackage } from './client/ClientPackages';
import ClientRepacks from './client/ClientRepacks';
import { ClientInvoices, ClientNotifyPayment, ClientMyPayments, ClientAccounts } from './client/ClientPayments';
import ClientAlerts, { ClientCreateAlert } from './client/ClientAlerts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientDashboard = () => {
  const { user, logout, token } = useAuth();
  const [currentSection, setCurrentSection] = useState('home');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { permission: notificationPermission, isSupported: notificationsSupported, requestPermission: requestNotificationPermission } = useNotifications(token);

  // Shared data
  const [trackings, setTrackings] = useState([]);
  const [warehouseReceipts, setWarehouseReceipts] = useState([]);
  const [repacks, setRepacks] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [mailbox, setMailbox] = useState(null);
  const [isLoadingMailbox, setIsLoadingMailbox] = useState(true);
  const [mailboxError, setMailboxError] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  // Form states kept here for cross-section use
  const [alertForm, setAlertForm] = useState({ tracking_number: '', description: '', store_origin: '', estimated_weight: '', declared_value: '', notes: '' });
  const [paymentForm, setPaymentForm] = useState({ payer_name: '', amount: '', payment_date: '', payment_method: '', reference_number: '', notes: '', receipt_file: null });
  const [deliveryAddress, setDeliveryAddress] = useState({ address: '', city: '', state: '', postal_code: '', phone: '', notes: '' });

  const axiosConfig = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const availableTrackings = trackings.filter(t => t.status === 'received');

  const fetchAllData = useCallback(async () => {
    try {
      const [trackingsRes, wrRes, repacksRes, invoicesRes, paymentsRes, alertsRes, accountsRes] = await Promise.all([
        axios.get(`${API}/client/trackings`, axiosConfig).catch(() => ({ data: [] })),
        axios.get(`${API}/client/warehouse-receipts`, axiosConfig).catch(() => ({ data: [] })),
        axios.get(`${API}/client/repacks`, axiosConfig).catch(() => ({ data: [] })),
        axios.get(`${API}/client/invoices`, axiosConfig).catch(() => ({ data: [] })),
        axios.get(`${API}/client/payments`, axiosConfig).catch(() => ({ data: [] })),
        axios.get(`${API}/client/alerts`, axiosConfig).catch(() => ({ data: [] })),
        axios.get(`${API}/client/accounts`, axiosConfig).catch(() => ({ data: [] })),
      ]);
      setTrackings(trackingsRes.data);
      setWarehouseReceipts(wrRes.data);
      setRepacks(repacksRes.data);
      setInvoices(invoicesRes.data);
      setPayments(paymentsRes.data);
      setAlerts(alertsRes.data);
      setAccounts(accountsRes.data);
    } catch (error) {
    }
  }, [axiosConfig]);

  const fetchMailboxWithRetry = useCallback(async (retries = 3) => {
    setIsLoadingMailbox(true);
    setMailboxError(null);
    for (let i = 0; i < retries; i++) {
      try {
        const res = await axios.get(`${API}/client/mailbox`, axiosConfig);
        setMailbox(res.data);
        setIsLoadingMailbox(false);

        if (res.data?.delivery_address) {
          setDeliveryAddress({
            address: res.data.delivery_address || '',
            city: res.data.delivery_city || '',
            state: res.data.delivery_state || '',
            postal_code: res.data.delivery_postal_code || '',
            phone: res.data.delivery_phone || '',
            notes: res.data.delivery_notes || ''
          });
        }
        return;
      } catch (error) {
        if (i === retries - 1) {
          setIsLoadingMailbox(false);
          setMailboxError('No se pudo cargar la informacion del casillero');
        } else {
          await new Promise(r => setTimeout(r, 1000 * (i + 1)));
        }
      }
    }
  }, [axiosConfig]);

  useEffect(() => {
    fetchAllData();
    fetchMailboxWithRetry();
  }, [fetchAllData, fetchMailboxWithRetry]);

  // Shared handlers
  const handleCreateAlert = async (e) => {
    e.preventDefault();
    try {
      const alertData = {
        tracking_number: alertForm.tracking_number,
        description: alertForm.description,
        store_origin: alertForm.store_origin || null,
        estimated_weight: alertForm.estimated_weight ? parseFloat(alertForm.estimated_weight) : null,
        declared_value: alertForm.declared_value ? parseFloat(alertForm.declared_value) : null,
        notes: alertForm.notes || null
      };
      await axios.post(`${API}/client/alerts`, alertData, axiosConfig);
      setAlertForm({ tracking_number: '', description: '', store_origin: '', estimated_weight: '', declared_value: '', notes: '' });
      fetchAllData();
      alert('Notificacion de paquete enviada exitosamente!');
    } catch (error) {
      alert('Error al crear notificacion: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleNotifyPayment = async (e) => {
    e.preventDefault();
    try {
      let receipt_file_url = null;
      let receipt_file_name = null;

      // Step 1: Upload file if present
      if (paymentForm.receipt_file) {
        const uploadData = new FormData();
        uploadData.append('file', paymentForm.receipt_file);
        const uploadRes = await axios.post(`${API}/upload/receipt`, uploadData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        receipt_file_url = uploadRes.data.url;
        receipt_file_name = uploadRes.data.filename;
      }

      // Step 2: Send payment as JSON
      const paymentData = {
        payer_name: paymentForm.payer_name,
        amount: parseFloat(paymentForm.amount),
        payment_date: paymentForm.payment_date,
        payment_method: paymentForm.payment_method,
        reference_number: paymentForm.reference_number,
        notes: paymentForm.notes || null,
        receipt_file_url: receipt_file_url,
        receipt_file_name: receipt_file_name
      };

      await axios.post(`${API}/client/payments`, paymentData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      setPaymentForm({ payer_name: '', amount: '', payment_date: '', payment_method: '', reference_number: '', notes: '', receipt_file: null });
      fetchAllData();
      alert('Pago notificado exitosamente!');
    } catch (error) {
      alert('Error al notificar pago: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleUpdateDeliveryAddress = async (e) => {
    e.preventDefault();
    try {
      const addressData = {
        delivery_address: deliveryAddress.address,
        delivery_city: deliveryAddress.city,
        delivery_state: deliveryAddress.state,
        delivery_phone: deliveryAddress.phone
      };
      await axios.put(`${API}/users/me/delivery-address`, addressData, axiosConfig);
      alert('Direccion de entrega actualizada exitosamente!');
    } catch (error) {
      alert('Error al actualizar direccion: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Mailbox renderer (shared between overview and profile)
  const renderMyMailbox = () => {
    const copyToClipboard = (text, fieldName) => {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(() => { setCopiedField(fieldName); setTimeout(() => setCopiedField(null), 2000); }).catch(() => fallbackCopy(text, fieldName));
      } else { fallbackCopy(text, fieldName); }
    };
    const fallbackCopy = (text, fieldName) => {
      const ta = document.createElement('textarea'); ta.value = text; ta.style.position = 'fixed'; ta.style.left = '-9999px';
      document.body.appendChild(ta); ta.focus(); ta.select();
      try { document.execCommand('copy'); setCopiedField(fieldName); setTimeout(() => setCopiedField(null), 2000); } catch { prompt('Copia este texto manualmente (Ctrl+C):', text); }
      document.body.removeChild(ta);
    };
    const getFullAddressText = () => {
      if (!mailbox) return '';
      const nameParts = (mailbox.client_full_name || '').split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || firstName;
      return `Nombre: Traetelo D Afuera\nApellido: ${firstName} ${lastName}\nDireccion: 8438 NW 66th Street\nApt/Suite/Unit: ${mailbox.mailbox_code}\nCiudad: Miami\nEstado: Florida\nCodigo Postal: 33166\nPais: United States\nTelefono: +1 786 828 3898`;
    };

    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Tu Casillero en Miami</CardTitle>
          <CardDescription>Usa estos datos para registrarte en tiendas online (Amazon, Shein, AliExpress, etc.)</CardDescription>
        </CardHeader>
        <CardContent>
          {mailbox ? (
            <div className="space-y-6">
              <div className="bg-blue-600 text-white p-6 rounded-lg text-center">
                <p className="text-sm opacity-90 mb-2">TU CODIGO DE CLIENTE</p>
                <p className="text-4xl font-bold tracking-wider">{mailbox.mailbox_code}</p>
                <p className="text-sm opacity-90 mt-2">INCLUIR EN TODAS TUS COMPRAS</p>
                <Button variant="secondary" className="mt-3" onClick={() => copyToClipboard(mailbox.mailbox_code, 'codigo')}>
                  {copiedField === 'codigo' ? 'Copiado!' : 'Copiar Codigo'}
                </Button>
              </div>
              <div className="bg-green-50 border-2 border-green-400 p-6 rounded-lg">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-bold text-green-800">Direccion de Envio (USA)</h3>
                  <Button className={copiedField === 'todo' ? 'bg-green-700' : 'bg-green-600 hover:bg-green-700'} onClick={() => copyToClipboard(getFullAddressText(), 'todo')}>
                    {copiedField === 'todo' ? 'Copiado!' : 'Copiar Todo'}
                  </Button>
                </div>
                <div className="bg-white rounded-lg border-2 border-green-300 overflow-hidden">
                  <table className="w-full text-sm">
                    <tbody>
                      {[
                        ['Nombre (First Name)', 'Traetelo D Afuera', 'nombre'],
                        ['Apellido (Last Name)', (() => { const p = (mailbox.client_full_name || '').split(' '); return `${p[0] || ''} ${p.slice(1).join(' ') || p[0] || ''}`; })(), 'apellido'],
                        ['Direccion (Address)', '8438 NW 66th Street', 'direccion'],
                      ].map(([label, value, key]) => (
                        <tr key={key} className="border-b border-green-100 hover:bg-green-50">
                          <td className="p-3 font-semibold text-gray-600 w-40">{label}</td>
                          <td className="p-3 font-bold">{value}</td>
                          <td className="p-3 w-24"><button onClick={() => copyToClipboard(value, key)} className="text-green-600 hover:text-green-800 text-xs bg-green-100 px-2 py-1 rounded">{copiedField === key ? 'Copiado' : 'Copiar'}</button></td>
                        </tr>
                      ))}
                      <tr className="border-b border-green-100 hover:bg-yellow-50 bg-yellow-50">
                        <td className="p-3 font-semibold text-gray-700">Apt/Suite/Unit</td>
                        <td className="p-3 font-bold text-blue-700 text-lg">{mailbox.mailbox_code}</td>
                        <td className="p-3"><button onClick={() => copyToClipboard(mailbox.mailbox_code, 'apt')} className="text-green-600 hover:text-green-800 text-xs bg-green-100 px-2 py-1 rounded">{copiedField === 'apt' ? 'Copiado' : 'Copiar'}</button></td>
                      </tr>
                      {[
                        ['Ciudad (City)', 'Miami', 'ciudad'],
                        ['Estado (State)', 'Florida', 'estado'],
                        ['Codigo Postal (ZIP)', '33166', 'zip'],
                        ['Pais (Country)', 'United States', 'pais'],
                        ['Telefono (Phone)', '+1 786 828 3898', 'telefono'],
                      ].map(([label, value, key]) => (
                        <tr key={key} className="border-b border-green-100 hover:bg-green-50">
                          <td className="p-3 font-semibold text-gray-600">{label}</td>
                          <td className="p-3 font-bold">{value}</td>
                          <td className="p-3"><button onClick={() => copyToClipboard(value, key)} className="text-green-600 hover:text-green-800 text-xs bg-green-100 px-2 py-1 rounded">{copiedField === key ? 'Copiado' : 'Copiar'}</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="bg-yellow-100 border-2 border-yellow-400 p-4 rounded-lg">
                <p className="text-yellow-800 font-semibold"><strong>MUY IMPORTANTE:</strong></p>
                <ul className="text-yellow-800 text-sm mt-2 space-y-1">
                  <li>- El campo <strong>Apt/Suite/Unit</strong> DEBE contener tu codigo: <strong>{mailbox.mailbox_code}</strong></li>
                  <li>- En algunas tiendas aparece como "Account Number" o "Reference"</li>
                  <li>- Sin este codigo NO podremos identificar tus paquetes</li>
                </ul>
              </div>
              {mailbox.venezuela_address && (
                <div className="bg-gray-50 p-4 rounded-lg border">
                  <h3 className="font-semibold text-lg mb-3">Tu Direccion de Entrega (Venezuela)</h3>
                  <p>{mailbox.venezuela_address}</p>
                  <p>{mailbox.venezuela_city}, {mailbox.venezuela_state}</p>
                </div>
              )}
            </div>
          ) : isLoadingMailbox ? (
            <div className="text-center py-12">
              <div className="animate-pulse">
                <div className="bg-blue-100 rounded-lg p-6 mb-4"><div className="h-8 bg-blue-200 rounded w-48 mx-auto mb-2"></div><div className="h-12 bg-blue-300 rounded w-64 mx-auto"></div></div>
              </div>
              <div className="mt-6 bg-blue-50 border-2 border-blue-300 rounded-lg p-4">
                <p className="text-blue-800 font-semibold text-lg">Bienvenido {user?.full_name?.split(' ')[0] || 'Cliente'}!</p>
                <p className="text-blue-600 mt-2">Estamos terminando de configurar tu casillero...</p>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <Package className="mx-auto h-16 w-16 text-gray-400 mb-4" />
              <p className="text-gray-500 text-lg">No tienes un casillero asignado aun</p>
              <p className="text-gray-400 text-sm mt-2">Contacta al administrador para obtener tu casillero</p>
              {mailboxError && (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-600 text-sm">{mailboxError}</p>
                  <Button variant="outline" className="mt-2" onClick={() => fetchMailboxWithRetry()}>Reintentar</Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  // Repacks use a custom hook pattern
  const repacksComponent = ClientRepacks({ token, user, trackings, repacks, onRefresh: fetchAllData, onNavigate: setCurrentSection });

  // Profile section (kept inline since it's small and uses many parent states)
  const renderProfile = () => (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle>Mis Datos</CardTitle><CardDescription>Informacion de tu cuenta</CardDescription></CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div><Label className="text-gray-600">Nombre Completo</Label><p className="text-lg font-semibold">{user?.full_name}</p></div>
            <div><Label className="text-gray-600">Email</Label><p className="text-lg font-semibold">{user?.email}</p></div>
            <div><Label className="text-gray-600">Rol</Label><p className="text-lg font-semibold capitalize">{user?.role}</p></div>
            <div><Label className="text-gray-600">Estado de Cuenta</Label><p className="text-lg font-semibold text-green-600">{user?.is_active ? 'Activa' : 'Inactiva'}</p></div>
          </div>
        </CardContent>
      </Card>
      {mailbox && renderMyMailbox()}
      <Card>
        <CardHeader><CardTitle>Direccion de Entrega</CardTitle><CardDescription>Direccion donde recibiras tus paquetes en Venezuela</CardDescription></CardHeader>
        <CardContent>
          <form onSubmit={handleUpdateDeliveryAddress} className="space-y-4">
            <div className="space-y-2"><Label>Direccion Completa *</Label><Input value={deliveryAddress.address} onChange={(e) => setDeliveryAddress({...deliveryAddress, address: e.target.value})} placeholder="Calle, Avenida, Edificio, Piso, Apartamento" required /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Ciudad *</Label><Input value={deliveryAddress.city} onChange={(e) => setDeliveryAddress({...deliveryAddress, city: e.target.value})} placeholder="Ciudad" required /></div>
              <div className="space-y-2"><Label>Estado *</Label><Input value={deliveryAddress.state} onChange={(e) => setDeliveryAddress({...deliveryAddress, state: e.target.value})} placeholder="Estado" required /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Codigo Postal</Label><Input value={deliveryAddress.postal_code} onChange={(e) => setDeliveryAddress({...deliveryAddress, postal_code: e.target.value})} placeholder="Codigo Postal" /></div>
              <div className="space-y-2"><Label>Telefono de Contacto *</Label><Input value={deliveryAddress.phone} onChange={(e) => setDeliveryAddress({...deliveryAddress, phone: e.target.value})} placeholder="Ej: +58 412 1234567" required /></div>
            </div>
            <div className="space-y-2"><Label>Notas adicionales (opcional)</Label><Textarea value={deliveryAddress.notes} onChange={(e) => setDeliveryAddress({...deliveryAddress, notes: e.target.value})} placeholder="Punto de referencia, instrucciones especiales, etc." rows={2} /></div>
            <Button type="submit" className="w-full">Guardar Direccion de Entrega</Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );

  const renderSection = () => {
    switch (currentSection) {
      case 'home':
        return <ClientOverview trackings={trackings} repacks={repacks} invoices={invoices} alerts={alerts} availableTrackings={availableTrackings} mailbox={mailbox} notificationPermission={notificationPermission} notificationsSupported={notificationsSupported} requestNotificationPermission={requestNotificationPermission} renderMyMailbox={renderMyMailbox} />;
      case 'packages':
        return <ClientPackages token={token} trackings={trackings} warehouseReceipts={warehouseReceipts} />;
      case 'warehouse-receipts':
        return <ClientWarehouseReceipts token={token} warehouseReceipts={warehouseReceipts} />;
      case 'track-package':
        return <ClientTrackPackage trackings={trackings} />;
      case 'my-mailbox':
        return renderMyMailbox();
      case 'repack-request':
        return repacksComponent.renderRequest();
      case 'my-repacks':
        return repacksComponent.renderList();
      case 'invoices':
        return <ClientInvoices token={token} invoices={invoices} />;
      case 'notify-payment':
        return <ClientNotifyPayment token={token} paymentForm={paymentForm} setPaymentForm={setPaymentForm} onSubmit={handleNotifyPayment} />;
      case 'my-payments':
        return <ClientMyPayments payments={payments} />;
      case 'accounts':
        return <ClientAccounts accounts={accounts} />;
      case 'alerts':
        return <ClientAlerts token={token} alerts={alerts} onRefresh={fetchAllData} />;
      case 'create-alert':
        return <ClientCreateAlert token={token} alertForm={alertForm} setAlertForm={setAlertForm} onSubmit={handleCreateAlert} />;
      case 'profile':
        return renderProfile();
      case 'update-profile':
        return (
          <Card>
            <CardHeader><CardTitle>Actualizar Datos</CardTitle><CardDescription>Modifica tu informacion personal</CardDescription></CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="space-y-2"><Label>Nombre Completo</Label><Input defaultValue={user?.full_name} /></div>
                <div className="space-y-2"><Label>Email</Label><Input type="email" defaultValue={user?.email} /></div>
                <div className="space-y-2"><Label>Nueva Contrasena</Label><Input type="password" placeholder="Dejar en blanco para no cambiar" /></div>
                <Button>Guardar Cambios</Button>
                <p className="text-xs text-gray-500">* Esta funcionalidad estara disponible proximamente</p>
              </div>
            </CardContent>
          </Card>
        );
      default:
        return <ClientOverview trackings={trackings} repacks={repacks} invoices={invoices} alerts={alerts} availableTrackings={availableTrackings} mailbox={mailbox} notificationPermission={notificationPermission} notificationsSupported={notificationsSupported} requestNotificationPermission={requestNotificationPermission} renderMyMailbox={renderMyMailbox} />;
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar userRole="client" onSectionChange={setCurrentSection} currentSection={currentSection} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 overflow-auto w-full">
        <header className="bg-white shadow-sm border-b sticky top-0 z-30" data-testid="client-dashboard-header">
          <div className="px-4 md:px-6 py-3 md:py-4 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <button onClick={() => setSidebarOpen(true)} className="md:hidden p-2 hover:bg-gray-100 rounded-lg"><Menu size={24} /></button>
              <div>
                <h1 className="text-lg md:text-2xl font-bold text-gray-900">Mi Casillero{mailbox?.mailbox_code && <span className="text-blue-600 ml-1 md:ml-2">: {mailbox.mailbox_code}</span>}</h1>
                <p className="text-xs md:text-sm text-gray-600 hidden sm:block">{user?.email}</p>
              </div>
            </div>
            <Button onClick={logout} variant="outline" size="sm" className="text-xs md:text-sm" data-testid="logout-button"><span className="hidden sm:inline">Cerrar </span>Sesion</Button>
          </div>
        </header>
        <main className="p-4 md:p-6">{renderSection()}</main>
      </div>
    </div>
  );
};

export default ClientDashboard;
